/* CODE Cellular Water — immersive motion system v3 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-code-immersion]';
  const SCENE_SELECTOR = '[data-code-scene]';
  const VIDEO_SELECTOR = '[data-code-video]';
  const SHELL_SELECTOR = '[data-code-media-shell]';
  const WEBGL_SELECTOR = '[data-code-webgl], .code-immersion__canvas';
  const MAX_ACTIVE_VIDEOS = 2;
  const instances = new WeakMap();

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const smoothstep = (from, to, value) => {
    const amount = clamp((value - from) / Math.max(to - from, 0.0001));
    return amount * amount * (3 - 2 * amount);
  };
  const easeOutCubic = (value) => 1 - Math.pow(1 - clamp(value), 3);
  const round = (value, precision = 4) => Number(value.toFixed(precision));

  function hydrateDeferredPoster(picture) {
    if (!picture?.hasAttribute('data-code-poster-deferred')) return;

    picture.querySelectorAll('[data-code-poster-srcset]').forEach((source) => {
      source.srcset = source.dataset.codePosterSrcset;
      source.removeAttribute('data-code-poster-srcset');
    });

    const image = picture.querySelector('img[data-code-poster-src]');
    if (image) {
      image.src = image.dataset.codePosterSrc;
      image.removeAttribute('data-code-poster-src');
    }

    picture.removeAttribute('data-code-poster-deferred');
    picture.setAttribute('data-code-poster-hydrated', '');
  }

  function listenMediaQuery(query, callback) {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', callback);
      return () => query.removeEventListener('change', callback);
    }

    query.addListener(callback);
    return () => query.removeListener(callback);
  }

  class ScrubController {
    constructor(video, requestFrame) {
      this.video = video;
      this.requestFrame = requestFrame;
      this.target = 0;
      this.pending = false;
      this.ready = false;
      this.onMetadata = this.onMetadata.bind(this);
      this.onSeeked = this.onSeeked.bind(this);

      video.addEventListener('loadedmetadata', this.onMetadata);
      video.addEventListener('seeked', this.onSeeked);
      if (video.readyState >= 1) this.onMetadata();
    }

    onMetadata() {
      this.ready = Number.isFinite(this.video.duration) && this.video.duration > 0;
      if (this.ready) this.seek();
    }

    onSeeked() {
      if (!this.pending) return;
      this.pending = false;
      this.seek();
    }

    setProgress(progress) {
      if (!this.ready) return;
      const usableDuration = Math.max(0, this.video.duration - 0.04);
      this.target = clamp(progress) * usableDuration;
      this.seek();
    }

    seek() {
      if (!this.ready || this.video.seeking) {
        this.pending = true;
        return;
      }

      if (Math.abs(this.video.currentTime - this.target) < 0.035) return;

      try {
        this.video.currentTime = this.target;
      } catch (_error) {
        // A browser can reject seeks while metadata is being replaced; the next
        // metadata/scroll frame retries without taking the rest of the page down.
        this.pending = true;
        this.requestFrame();
      }
    }

    destroy() {
      this.video.removeEventListener('loadedmetadata', this.onMetadata);
      this.video.removeEventListener('seeked', this.onSeeked);
    }
  }

  class MediaPool {
    constructor(root, options) {
      this.root = root;
      this.mobileQuery = options.mobileQuery;
      this.requestFrame = options.requestFrame;
      this.items = Array.from(root.querySelectorAll(VIDEO_SELECTOR))
        .map((video) => ({
          video,
          shell: video.closest(SHELL_SELECTOR) || video.parentElement,
          scene: video.closest(SCENE_SELECTOR),
          hydrated: false,
          ready: false,
          failed: false,
          playPending: false,
          active: false,
          scrubber: null,
          activeSources: [],
          failedSources: new Set(),
          resetActiveSources: null,
        }));
      this.activeVideos = new Map();
      this.itemByScene = new Map(this.items.filter((item) => item.scene).map((item) => [item.scene, item]));
      this.destroyCallbacks = [];
    }

    sourcesFor(video) {
      return Array.from(video.querySelectorAll('source[data-code-src]'));
    }

    activateSources(video) {
      const sources = this.sourcesFor(video);
      sources.forEach((source) => {
        const matchesViewport = !source.media || window.matchMedia(source.media).matches;
        if (matchesViewport) source.src = source.dataset.codeSrc;
        else source.removeAttribute('src');
      });
      return sources.filter((source) => source.hasAttribute('src'));
    }

    hydrate(item) {
      if (!item || item.hydrated || item.failed) return;

      const sources = this.sourcesFor(item.video);
      const directSource = item.video.dataset.codeSrc;
      if (!sources.length && !directSource) {
        this.fail(item, 'missing-source');
        return;
      }

      item.hydrated = true;
      item.shell?.classList.add('is-code-video-loading');
      item.video.preload = item.scene?.dataset.codeScene === 'hero' ? 'auto' : 'metadata';
      item.video.muted = true;
      item.video.playsInline = true;
      item.video.loop = false;
      item.video.controls = false;

      const onSourceError = (event) => {
        if (!item.activeSources.includes(event.currentTarget)) return;
        item.failedSources.add(event.currentTarget);
        if (item.activeSources.length && item.failedSources.size >= item.activeSources.length) {
          this.fail(item, 'source-error');
        }
      };
      sources.forEach((source) => source.addEventListener('error', onSourceError));
      item.resetActiveSources = () => {
        item.failedSources.clear();
        item.activeSources = this.activateSources(item.video);
        return item.activeSources;
      };

      if (directSource) item.video.src = directSource;
      if (sources.length && !item.resetActiveSources().length) {
        this.fail(item, 'viewport-source');
        return;
      }

      const revealVideo = () => {
        item.shell?.classList.remove('is-code-video-loading', 'is-code-video-failed');
        item.shell?.classList.add('is-code-video-ready');
      };

      const onFirstSeeked = () => revealVideo();
      const onLoadedData = () => {
        if (this.mobileQuery.matches) revealVideo();
      };

      const onMetadata = () => {
        item.ready = true;
        item.shell?.classList.remove('is-code-video-failed', 'is-code-video-ready');
        item.shell?.classList.add('is-code-video-loading');
        item.scrubber?.destroy();
        item.scrubber = null;
        item.video.removeEventListener('seeked', onFirstSeeked);
        item.video.removeEventListener('loadeddata', onLoadedData);
        if (!this.mobileQuery.matches) {
          item.video.pause();
          // Keep the poster visible until the browser has decoded the requested
          // scrub frame. Revealing on metadata alone produced a black flash on
          // slower connections because no drawable frame existed yet.
          item.video.addEventListener('seeked', onFirstSeeked, { once: true });
          item.scrubber = new ScrubController(item.video, this.requestFrame);
        } else {
          if (item.video.readyState >= 2) revealVideo();
          else item.video.addEventListener('loadeddata', onLoadedData, { once: true });
          if (item.scene?.classList.contains('is-code-scene-active')) this.playOnce(item);
        }
        this.requestFrame();
      };

      const onError = () => this.fail(item, 'decode-error');
      const onEnded = () => {
        this.activeVideos.delete(item.video);
        item.shell?.classList.add('is-code-video-ended');
      };

      item.video.addEventListener('loadedmetadata', onMetadata);
      item.video.addEventListener('error', onError);
      item.video.addEventListener('ended', onEnded);
      this.destroyCallbacks.push(() => {
        item.video.removeEventListener('loadedmetadata', onMetadata);
        item.video.removeEventListener('loadeddata', onLoadedData);
        item.video.removeEventListener('seeked', onFirstSeeked);
        item.video.removeEventListener('error', onError);
        item.video.removeEventListener('ended', onEnded);
        sources.forEach((source) => source.removeEventListener('error', onSourceError));
      });
      item.video.load();
    }

    fail(item, reason) {
      item.failed = true;
      item.ready = false;
      this.pause(item);
      item.shell?.classList.remove('is-code-video-loading', 'is-code-video-ready');
      item.shell?.classList.add('is-code-video-failed');
      item.shell?.setAttribute('data-code-video-fallback', reason);
      // Posters and the underlying editorial images remain visible when a codec,
      // autoplay policy, or CDN request fails. Video is enhancement, never content.
    }

    pause(item) {
      if (!item) return;
      if (!item.video.paused) item.video.pause();
      this.activeVideos.delete(item.video);
    }

    async playOnce(item) {
      if (!item?.ready || item.failed || document.hidden) return;
      if (item.video.ended || item.video.currentTime >= item.video.duration - 0.08) return;
      if (item.shell?.classList.contains('is-code-video-autoplay-blocked')) return;
      if (!item.video.paused || item.playPending) return;

      item.playPending = true;
      item.video.loop = false;
      item.video.playbackRate = 0.78;

      try {
        await item.video.play();
        if (!item.active) {
          this.pause(item);
          return;
        }
        this.activeVideos.set(item.video, performance.now());

        if (this.activeVideos.size > MAX_ACTIVE_VIDEOS) {
          const oldest = [...this.activeVideos.entries()].sort((a, b) => a[1] - b[1])[0]?.[0];
          const oldestItem = this.items.find((candidate) => candidate.video === oldest);
          if (oldestItem && oldestItem !== item) this.pause(oldestItem);
        }
      } catch (error) {
        // Autoplay rejection is not a media failure. Keep the poster rather than
        // repeatedly prompting playback or creating a scroll-loop on mobile.
        if (error?.name === 'NotAllowedError') {
          item.shell?.classList.add('is-code-video-autoplay-blocked');
        }
      } finally {
        item.playPending = false;
      }
    }

    update(scene, state) {
      const item = this.itemByScene.get(scene);
      if (!item) return;
      const playbackActive = this.mobileQuery.matches ? state.focused : state.active;
      item.active = playbackActive;

      // The first chapter sits inside Chrome's generous lazy-load distance on
      // slow connections. Keep its poster out of the initial request queue,
      // then restore it as soon as the chapter actually reaches the viewport.
      if (state.active) hydrateDeferredPoster(item.shell?.querySelector('[data-code-poster-deferred]'));

      const isHero = item.scene?.dataset.codeScene === 'hero';
      const hasEnteredHeroStory = state.progress > 0.015 || window.scrollY > 4;
      const shouldHydrate = this.mobileQuery.matches
        ? (isHero ? hasEnteredHeroStory : state.focused)
        : (!isHero || hasEnteredHeroStory);

      // Keep the fast, high-quality poster as the initial LCP candidate. The
      // hero movie hydrates as soon as the visitor starts the scroll story on
      // either viewport. On mobile, later chapters hydrate only when they enter
      // the focus band, so a next-scene video cannot compete with first paint.
      if (state.near && shouldHydrate) this.hydrate(item);
      if (!item.ready) return;

      if (this.mobileQuery.matches) {
        if (playbackActive) this.playOnce(item);
        else this.pause(item);
        return;
      }

      this.pause(item);
      item.scrubber?.setProgress(state.progress);
    }

    onViewportModeChange() {
      this.items.forEach((item) => {
        this.pause(item);
        item.scrubber?.destroy();
        item.scrubber = null;

        if (!item.hydrated || item.failed) return;
        item.ready = false;
        item.shell?.classList.remove('is-code-video-ready');
        item.shell?.classList.add('is-code-video-loading');
        if (item.resetActiveSources && !item.resetActiveSources().length) {
          this.fail(item, 'viewport-source');
          return;
        }
        item.video.load();
      });
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.items.forEach((item) => this.pause(item));
        return;
      }

      if (!this.mobileQuery.matches) return;
      this.items
        .filter((item) => item.active)
        .forEach((item) => this.playOnce(item));
    }

    destroy() {
      this.items.forEach((item) => {
        this.pause(item);
        item.scrubber?.destroy();
      });
      this.destroyCallbacks.forEach((callback) => callback());
    }
  }

  class FluidTransitionRenderer {
    constructor(canvas) {
      this.canvas = canvas;
      this.gl = null;
      this.program = null;
      this.uniforms = {};
      this.frame = 0;
      this.running = false;
      this.visible = true;
      this.lastDraw = 0;
      this.startedAt = performance.now();
      this.pageProgress = 0;
      this.sceneProgress = 0;
      this.sceneIndex = 0;
      this.pointer = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };
      this.draw = this.draw.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onContextLost = this.onContextLost.bind(this);
      this.onContextRestored = this.onContextRestored.bind(this);
    }

    init() {
      if (!this.canvas) return false;

      try {
        this.gl = this.canvas.getContext('webgl2', {
          alpha: true,
          antialias: false,
          depth: false,
          stencil: false,
          premultipliedAlpha: true,
          powerPreference: 'high-performance',
        });
        if (!this.gl) throw new Error('WebGL2 unavailable');

        const vertex = `#version 300 es
          precision highp float;
          const vec2 POSITIONS[3] = vec2[3](
            vec2(-1.0, -1.0),
            vec2(3.0, -1.0),
            vec2(-1.0, 3.0)
          );
          out vec2 vUv;
          void main() {
            vec2 position = POSITIONS[gl_VertexID];
            vUv = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
          }
        `;

        const fragment = `#version 300 es
          precision highp float;
          in vec2 vUv;
          out vec4 outColor;
          uniform vec2 uResolution;
          uniform vec2 uPointer;
          uniform float uTime;
          uniform float uPage;
          uniform float uScene;
          uniform float uSceneIndex;

          float hash21(vec2 p) {
            p = fract(p * vec2(123.34, 345.45));
            p += dot(p, p + 34.345);
            return fract(p.x * p.y);
          }

          float noise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            f = f * f * (3.0 - 2.0 * f);
            return mix(
              mix(hash21(i), hash21(i + vec2(1.0, 0.0)), f.x),
              mix(hash21(i + vec2(0.0, 1.0)), hash21(i + 1.0), f.x),
              f.y
            );
          }

          float fbm(vec2 p) {
            float value = 0.0;
            float amplitude = 0.52;
            mat2 rotation = mat2(0.82, -0.57, 0.57, 0.82);
            for (int i = 0; i < 4; i++) {
              value += amplitude * noise(p);
              p = rotation * p * 2.03 + 7.1;
              amplitude *= 0.48;
            }
            return value;
          }

          void main() {
            vec2 uv = vUv;
            float aspect = uResolution.x / max(uResolution.y, 1.0);
            vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);
            vec2 pointer = vec2((uPointer.x - 0.5) * aspect, uPointer.y - 0.5);
            float pointerField = exp(-dot(p - pointer, p - pointer) * 8.0);
            float tempo = uTime * 0.045;
            float phase = uPage * 2.2 + uScene * 0.8 + uSceneIndex * 0.37;
            vec2 drift = vec2(
              fbm(p * 2.2 + vec2(tempo, phase)),
              fbm(p * 2.4 + vec2(-phase, tempo * 0.7))
            ) - 0.5;
            p += drift * 0.075 + (pointer - p) * pointerField * 0.012;

            float body = fbm(p * 3.15 + vec2(phase * 0.25, -tempo));
            float fine = fbm(p * 7.2 - vec2(tempo * 1.8, phase * 0.45));
            float caustic = smoothstep(0.52, 0.84, body * 0.72 + fine * 0.38);
            float veil = smoothstep(0.18, 0.96, 1.0 - length(p) * 0.72);
            float pulse = 0.82 + 0.18 * sin(uTime * 0.19 + phase * 3.14);
            float alpha = caustic * veil * pulse * (0.026 + 0.018 * uScene);

            vec3 cool = vec3(0.30, 0.46, 0.48);
            vec3 warm = vec3(0.68, 0.53, 0.34);
            vec3 color = mix(cool, warm, clamp(uPage * 0.72 + body * 0.25, 0.0, 1.0));
            outColor = vec4(color * alpha, alpha);
          }
        `;

        const vertexShader = this.compile(this.gl.VERTEX_SHADER, vertex);
        const fragmentShader = this.compile(this.gl.FRAGMENT_SHADER, fragment);
        this.program = this.gl.createProgram();
        this.gl.attachShader(this.program, vertexShader);
        this.gl.attachShader(this.program, fragmentShader);
        this.gl.linkProgram(this.program);
        this.gl.deleteShader(vertexShader);
        this.gl.deleteShader(fragmentShader);

        if (!this.gl.getProgramParameter(this.program, this.gl.LINK_STATUS)) {
          throw new Error(this.gl.getProgramInfoLog(this.program) || 'WebGL link failed');
        }

        ['uResolution', 'uPointer', 'uTime', 'uPage', 'uScene', 'uSceneIndex'].forEach((name) => {
          this.uniforms[name] = this.gl.getUniformLocation(this.program, name);
        });

        this.gl.useProgram(this.program);
        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA);
        this.gl.clearColor(0, 0, 0, 0);
        this.canvas.addEventListener('webglcontextlost', this.onContextLost, false);
        this.canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);
        window.addEventListener('pointermove', this.onPointerMove, { passive: true });
        this.resize();
        this.canvas.classList.add('is-code-webgl-ready');
        return true;
      } catch (_error) {
        this.disable('initialization');
        return false;
      }
    }

    compile(type, source) {
      const shader = this.gl.createShader(type);
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);
      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        const message = this.gl.getShaderInfoLog(shader) || 'WebGL shader compilation failed';
        this.gl.deleteShader(shader);
        throw new Error(message);
      }
      return shader;
    }

    resize() {
      if (!this.gl) return;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);
      const width = Math.max(1, Math.round(window.innerWidth * pixelRatio));
      const height = Math.max(1, Math.round(window.innerHeight * pixelRatio));
      if (this.canvas.width === width && this.canvas.height === height) return;
      this.canvas.width = width;
      this.canvas.height = height;
      this.gl.viewport(0, 0, width, height);
    }

    setState(pageProgress, sceneProgress, sceneIndex) {
      this.pageProgress = pageProgress;
      this.sceneProgress = sceneProgress;
      this.sceneIndex = sceneIndex;
    }

    onPointerMove(event) {
      this.pointer.tx = clamp(event.clientX / Math.max(window.innerWidth, 1));
      this.pointer.ty = clamp(1 - event.clientY / Math.max(window.innerHeight, 1));
    }

    onContextLost(event) {
      event.preventDefault();
      this.stop();
      this.canvas.classList.add('is-code-webgl-failed');
    }

    onContextRestored() {
      // A restored context has no prior program state. The static image stack is
      // intentionally retained instead of risking a half-restored visual layer.
      this.disable('context-lost');
    }

    start() {
      if (!this.gl || this.running || !this.visible || document.hidden) return;
      this.running = true;
      this.frame = window.requestAnimationFrame(this.draw);
    }

    stop() {
      this.running = false;
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    setVisible(visible) {
      this.visible = visible;
      if (visible) this.start();
      else this.stop();
    }

    draw(now) {
      if (!this.running || !this.gl || document.hidden) {
        this.stop();
        return;
      }

      this.frame = window.requestAnimationFrame(this.draw);
      if (now - this.lastDraw < 30) return;
      this.lastDraw = now;
      this.pointer.x = lerp(this.pointer.x, this.pointer.tx, 0.055);
      this.pointer.y = lerp(this.pointer.y, this.pointer.ty, 0.055);

      this.gl.useProgram(this.program);
      this.gl.uniform2f(this.uniforms.uResolution, this.canvas.width, this.canvas.height);
      this.gl.uniform2f(this.uniforms.uPointer, this.pointer.x, this.pointer.y);
      this.gl.uniform1f(this.uniforms.uTime, (now - this.startedAt) / 1000);
      this.gl.uniform1f(this.uniforms.uPage, this.pageProgress);
      this.gl.uniform1f(this.uniforms.uScene, this.sceneProgress);
      this.gl.uniform1f(this.uniforms.uSceneIndex, this.sceneIndex);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
      this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
    }

    onVisibilityChange() {
      if (document.hidden) this.stop();
      else this.start();
    }

    disable(reason) {
      this.stop();
      this.canvas?.classList.remove('is-code-webgl-ready');
      this.canvas?.classList.add('is-code-webgl-failed');
      this.canvas?.setAttribute('data-code-webgl-fallback', reason);
      // WebGL is a transparent atmosphere layer. Removing it leaves all text,
      // imagery, links, and native scrolling fully usable on older/unstable GPUs.
      this.gl = null;
    }

    destroy() {
      this.stop();
      window.removeEventListener('pointermove', this.onPointerMove);
      this.canvas?.removeEventListener('webglcontextlost', this.onContextLost);
      this.canvas?.removeEventListener('webglcontextrestored', this.onContextRestored);
      if (this.gl && this.program) this.gl.deleteProgram(this.program);
      this.gl = null;
    }
  }

  class SceneController {
    constructor(root, mediaPool, renderer) {
      this.root = root;
      this.mediaPool = mediaPool;
      this.renderer = renderer;
      this.scenes = Array.from(root.querySelectorAll(SCENE_SELECTOR));
      this.metrics = [];
      this.rootMetric = { top: 0, height: 1 };
      this.viewportHeight = window.innerHeight;
      this.scrollY = window.scrollY;
      this.previousScrollY = this.scrollY;
      this.progressLabel = root.querySelector('[data-code-progress-label]');
      this.lastProgressLabel = '';
      this.lastActiveScene = '';
      this.frame = 0;
      this.measureFrame = 0;
      this.resizeTimer = 0;
      this.activeIndex = 0;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.render = this.render.bind(this);
      this.measure = this.measure.bind(this);
      this.requestFrame = this.requestFrame.bind(this);
      this.requestMeasure = this.requestMeasure.bind(this);
      this.resizeObserver = null;
    }

    init() {
      this.scenes.forEach((scene, index) => {
        scene.style.setProperty('--code-scene-index', String(index));
        scene._codeProductCards = Array.from(scene.querySelectorAll('[data-code-product-card]'));
        scene._codeFragments = Array.from(scene.querySelectorAll('[data-code-fragment]'));
        scene._codeProductCards.forEach((card, cardIndex) => {
          card.style.setProperty('--code-card-index', String(cardIndex));
        });
      });

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.requestMeasure);
        this.resizeObserver.observe(this.root);
        this.scenes.forEach((scene) => this.resizeObserver.observe(scene));
      }

      this.measure();
    }

    requestFrame() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.render);
    }

    requestMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = window.requestAnimationFrame(this.measure);
    }

    onScroll() {
      this.scrollY = window.scrollY;
      this.requestFrame();
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(this.requestMeasure, 90);
    }

    measure() {
      this.measureFrame = 0;
      this.viewportHeight = window.innerHeight;
      this.scrollY = window.scrollY;
      const rootBounds = this.root.getBoundingClientRect();
      this.rootMetric = {
        top: rootBounds.top + this.scrollY,
        height: Math.max(rootBounds.height, 1),
      };
      this.metrics = this.scenes.map((scene) => {
        const bounds = scene.getBoundingClientRect();
        const top = bounds.top + this.scrollY;
        return { scene, top, height: Math.max(bounds.height, 1), bottom: top + Math.max(bounds.height, 1) };
      });
      this.renderer?.resize();
      this.render();
    }

    progressFor(metric, index) {
      const available = metric.height - this.viewportHeight;
      const isOpeningScene = index === 0 && Math.abs(metric.top - this.rootMetric.top) < 4;
      if (isOpeningScene && available > 1) {
        return clamp((this.scrollY - metric.top) / available);
      }
      return clamp((this.scrollY + this.viewportHeight - metric.top) / (metric.height + this.viewportHeight));
    }

    applyMotion(metric, progress) {
      const scene = metric.scene;
      const id = scene.dataset.codeScene || '';
      const reveal = easeOutCubic(clamp((progress - 0.035) / 0.28));
      const heroExit = smoothstep(0.6, 0.88, progress);
      const copyOpacity = id === 'hero' ? 1 - heroExit : reveal;
      const mediaY = id === 'hero' ? (0.5 - progress) * 18 : (0.5 - progress) * 34;
      const mediaScale = id === 'hero' ? 1.025 + progress * 0.035 : 1.052 - progress * 0.03;
      const copyY = id === 'hero' ? -18 * heroExit : (1 - reveal) * 30;

      scene.style.setProperty('--code-scene-progress', String(round(progress)));
      scene.style.setProperty('--code-scene-y', `${round((0.5 - progress) * 18, 2)}px`);
      scene.style.setProperty('--code-media-y', `${round(mediaY, 2)}px`);
      scene.style.setProperty('--code-media-scale', String(round(mediaScale)));
      scene.style.setProperty('--code-copy-y', `${round(copyY, 2)}px`);
      scene.style.setProperty('--code-copy-opacity', String(round(copyOpacity)));
      scene.style.setProperty('--code-rail-progress', String(round(progress)));

      if (id === 'hero') {
        const impact = smoothstep(0.18, 0.64, progress);
        scene.style.setProperty('--code-hero-line-progress', String(round(smoothstep(0.015, 0.2, progress))));
        scene.style.setProperty('--code-refraction-opacity', String(round(0.2 + Math.sin(impact * Math.PI) * 0.5)));
        scene.style.setProperty('--code-refraction-x', `${round((progress - 0.5) * 14, 2)}px`);
        scene.style.setProperty('--code-refraction-y', `${round(-Math.sin(impact * Math.PI) * 9, 2)}px`);
        scene.style.setProperty('--code-refraction-scale', String(round(0.96 + impact * 0.14)));
      } else if (id === 'observation') {
        scene.style.setProperty('--code-observation-mask', `${round((1 - reveal) * 13, 2)}%`);
        scene.style.setProperty('--code-observation-line-progress', String(round(smoothstep(0.08, 0.7, progress))));
      } else if (id === 'origin') {
        const handoff = smoothstep(0.18, 0.8, progress);
        scene.style.setProperty('--code-origin-mist-opacity', String(round(0.96 - handoff * 0.54)));
        scene.style.setProperty('--code-origin-dew-opacity', String(round(0.3 + handoff * 0.68)));
        scene.style.setProperty('--code-origin-mist-y', `${round(-22 * handoff, 2)}px`);
        scene.style.setProperty('--code-origin-dew-y', `${round(26 - 38 * handoff, 2)}px`);
        scene.style.setProperty('--code-origin-mist-scale', String(round(1.06 - handoff * 0.018)));
        scene.style.setProperty('--code-origin-dew-scale', String(round(1.025 + handoff * 0.025)));
        scene.style.setProperty('--code-origin-mist-mask', `${round(handoff * 4, 2)}%`);
        scene.style.setProperty('--code-origin-dew-mask', `${round((1 - handoff) * 5, 2)}%`);
      } else if (id === 'process') {
        scene.style.setProperty('--code-process-mask', `${round((1 - reveal) * 15, 2)}%`);
        scene.style.setProperty('--code-process-media-x', `${round((0.5 - progress) * 38, 2)}px`);
        scene.style.setProperty('--code-process-copy-x', `${round((1 - reveal) * 28, 2)}px`);
        scene.style.setProperty('--code-process-rule-height', `${round(20 + progress * 66, 2)}%`);
      } else if (id === 'technical') {
        scene.style.setProperty('--code-diagram-mask', `${round((1 - reveal) * 14, 2)}%`);
        scene.style.setProperty('--code-diagram-opacity', String(round(0.5 + reveal * 0.5)));
        scene.style.setProperty('--code-diagram-y', `${round((1 - reveal) * 26, 2)}px`);
        scene.style.setProperty('--code-step-opacity', String(round(0.42 + reveal * 0.58)));
        scene.style.setProperty('--code-step-y', `${round((1 - reveal) * 18, 2)}px`);
      } else if (id === 'archive') {
        scene._codeProductCards.forEach((card, index) => {
          const cardReveal = easeOutCubic(clamp((progress * 1.35 - index * 0.045 - 0.04) / 0.34));
          card.style.setProperty('--code-product-opacity', String(round(0.2 + cardReveal * 0.8)));
          card.style.setProperty('--code-product-y', `${round((1 - cardReveal) * (28 + (index % 3) * 5), 2)}px`);
          card.style.setProperty('--code-product-scale', String(round(0.985 + cardReveal * 0.015)));
        });
      } else if (id === 'field') {
        const specimenReveal = smoothstep(0.12, 0.7, progress);
        scene.style.setProperty('--code-field-x', `${round((0.5 - progress) * 34, 2)}px`);
        scene.style.setProperty('--code-field-specimen-opacity', String(round(0.18 + specimenReveal * 0.82)));
        scene.style.setProperty('--code-field-specimen-y', `${round((1 - specimenReveal) * 30, 2)}px`);
        scene.style.setProperty('--code-field-specimen-scale', String(round(0.975 + specimenReveal * 0.025)));
        scene.style.setProperty('--code-field-specimen-clip', `${round((1 - specimenReveal) * 8, 2)}%`);
      } else if (id === 'fragments') {
        const fragmentWindowReveal = smoothstep(0.08, 0.58, progress);
        scene.style.setProperty('--code-fragments-x', `${round(-Math.min(360, window.innerWidth * 0.28) * progress, 2)}px`);
        scene.style.setProperty('--code-fragment-window-opacity', String(round(0.16 + fragmentWindowReveal * 0.84)));
        scene.style.setProperty('--code-fragment-window-x', `${round((1 - fragmentWindowReveal) * 42, 2)}px`);
        scene.style.setProperty('--code-fragment-window-clip', `${round((1 - fragmentWindowReveal) * 9, 2)}%`);
        scene._codeFragments.forEach((fragment, index) => {
          const target = scene._codeFragments.length > 1 ? index / (scene._codeFragments.length - 1) : 0;
          const focus = Math.max(0, 1 - Math.abs(progress - target) * 4.2);
          fragment.style.setProperty('--code-fragment-opacity', String(round(0.64 + focus * 0.36)));
        });
      } else if (id === 'exit') {
        scene.style.setProperty('--code-exit-copy-scale', String(round(0.965 + reveal * 0.035)));
      }

      const namedVariable = {
        hero: '--code-hero-progress',
        observation: '--code-observation-progress',
        origin: '--code-origin-progress',
        process: '--code-process-progress',
        technical: '--code-technical-progress',
        archive: '--code-product-progress',
        field: '--code-field-progress',
        fragments: '--code-fragments-progress',
        exit: '--code-exit-progress',
      }[id];
      if (namedVariable) scene.style.setProperty(namedVariable, String(round(progress)));
    }

    render() {
      this.frame = 0;
      if (!this.metrics.length) return;

      const pageTravel = Math.max(this.rootMetric.height - this.viewportHeight, 1);
      const pageProgress = clamp((this.scrollY - this.rootMetric.top) / pageTravel);
      const viewportTop = this.scrollY;
      const viewportBottom = this.scrollY + this.viewportHeight;
      const viewportCenter = this.scrollY + this.viewportHeight * 0.5;
      const direction = this.scrollY >= this.previousScrollY ? 'forward' : 'reverse';
      this.previousScrollY = this.scrollY;
      this.root.dataset.codeScrollDirection = direction;
      this.root.style.setProperty('--code-page-progress', String(round(pageProgress)));

      let closestDistance = Number.POSITIVE_INFINITY;
      let activeProgress = 0;

      this.metrics.forEach((metric, index) => {
        const progress = this.progressFor(metric, index);
        const active = metric.bottom > viewportTop && metric.top < viewportBottom;
        const near = metric.bottom > viewportTop - this.viewportHeight * 1.25
          && metric.top < viewportBottom + this.viewportHeight * 1.5;
        const focusTop = viewportTop + this.viewportHeight * 0.18;
        const focusBottom = viewportBottom - this.viewportHeight * 0.18;
        const focused = metric.bottom > focusTop && metric.top < focusBottom;
        const distance = Math.abs(metric.top + metric.height * 0.5 - viewportCenter);

        metric.scene.classList.toggle('is-code-scene-active', active);
        metric.scene.toggleAttribute('data-code-scene-active', active);
        this.applyMotion(metric, progress);
        this.mediaPool?.update(metric.scene, { active, focused, near, progress });

        if (distance < closestDistance) {
          closestDistance = distance;
          this.activeIndex = index;
          activeProgress = progress;
        }
      });

      const activeLabel = this.metrics[this.activeIndex]?.scene.dataset.codeSceneLabel;
      const activeScene = this.metrics[this.activeIndex]?.scene.dataset.codeScene;
      if (activeLabel && activeLabel !== this.lastProgressLabel) {
        if (this.progressLabel) this.progressLabel.textContent = activeLabel;
        this.lastProgressLabel = activeLabel;
      }
      if (activeScene && activeScene !== this.lastActiveScene) {
        this.root.dataset.codeActiveScene = activeScene;
        this.lastActiveScene = activeScene;
      }
      this.renderer?.setState(pageProgress, activeProgress, this.activeIndex);
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame);
      this.resizeObserver?.disconnect();
    }
  }

  class ImmersionApp {
    constructor(root) {
      this.root = root;
      this.html = document.documentElement;
      this.reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.saveData = Boolean(navigator.connection?.saveData);
      this.reduced = this.saveData || this.reduceQuery.matches;
      this.mediaPool = null;
      this.renderer = null;
      this.sceneController = null;
      this.visibilityObserver = null;
      this.unlistenMode = null;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
    }

    init() {
      if (this.root.dataset.codeImmersionInitialized === 'true') return;
      this.root.dataset.codeImmersionInitialized = 'true';

      if (this.reduced) {
        this.applyStaticMode();
        return;
      }

      this.html.classList.add('code-immersion-enhanced');
      this.root.classList.add('is-code-immersion-enhanced');
      this.root.dataset.codeImmersionMode = 'enhanced';

      let requestSceneFrame = () => {};
      this.mediaPool = new MediaPool(this.root, {
        mobileQuery: this.mobileQuery,
        requestFrame: () => requestSceneFrame(),
      });

      const canvas = this.root.querySelector(WEBGL_SELECTOR);
      this.renderer = new FluidTransitionRenderer(canvas);
      if (!this.renderer.init()) this.renderer = null;

      this.sceneController = new SceneController(this.root, this.mediaPool, this.renderer);
      requestSceneFrame = this.sceneController.requestFrame;
      this.sceneController.init();
      this.renderer?.start();

      if ('IntersectionObserver' in window && this.renderer) {
        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => this.renderer?.setVisible(entry.isIntersecting),
          { rootMargin: '25% 0px 25% 0px', threshold: 0 },
        );
        this.visibilityObserver.observe(this.root);
      }

      this.unlistenMode = listenMediaQuery(this.mobileQuery, this.onModeChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    applyStaticMode() {
      this.html.classList.add('code-immersion-reduced', 'code-immersion-static');
      if (this.saveData) this.html.classList.add('code-immersion-save-data', 'code-save-data');
      this.root.classList.add('is-code-immersion-static', 'code-immersion--static', 'code-immersion-no-sticky');
      this.root.dataset.codeMode = 'static';
      this.root.dataset.codeImmersionMode = this.saveData ? 'save-data' : 'reduced-motion';
      this.root.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
        video.preload = 'none';
        video.pause();
      });
      this.root.querySelectorAll('[data-code-poster-deferred]').forEach(hydrateDeferredPoster);
      // No video source is hydrated and no WebGL context is requested here.
      // The explicit no-sticky classes let CSS return to a plain document flow.
    }

    onModeChange() {
      this.mediaPool?.onViewportModeChange();
      this.sceneController?.requestMeasure();
    }

    onVisibilityChange() {
      this.mediaPool?.onVisibilityChange();
      this.renderer?.onVisibilityChange();
    }

    destroy() {
      this.unlistenMode?.();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.visibilityObserver?.disconnect();
      this.sceneController?.destroy();
      this.mediaPool?.destroy();
      this.renderer?.destroy();
      this.root.removeAttribute('data-code-immersion-initialized');
      instances.delete(this.root);
    }
  }

  function boot(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(ROOT_SELECTOR) || []);
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const app = new ImmersionApp(root);
      instances.set(root, app);
      app.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    instances.get(root)?.destroy();
  });
})();
